import { Pencil, Shield, Trash2, UserPlus } from 'lucide-react'
import { useState } from 'react'
import { AddAdminModal } from '@/components/admin-users/add-admin-modal'
import { ChangeRoleModal } from '@/components/admin-users/change-role-modal'
import { RemoveAdminModal } from '@/components/admin-users/remove-admin-modal'
import { DataTable } from '@/components/ui/data-table'
import { LoadingPage } from '@/components/ui/loading'
import { StatusBadge } from '@/components/ui/status-badge'
import { useAdminUsers } from '@/hooks/use-admin-users'
import { useAuth } from '@/lib/auth-context'
import type { AdminUserRecord } from '@/lib/types'

export function AdminUsersPage(): React.JSX.Element {
  const { isSuperAdmin } = useAuth()
  const usersQuery = useAdminUsers()
  const [showAddModal, setShowAddModal] = useState(false)
  const [removingUser, setRemovingUser] = useState<AdminUserRecord | null>(null)
  const [changingRole, setChangingRole] = useState<AdminUserRecord | null>(null)

  if (!isSuperAdmin) {
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <Shield className="mb-4 h-12 w-12 text-text-tertiary" />
        <h2 className="text-lg font-semibold text-text-primary">Access Denied</h2>
        <p className="mt-1 text-sm text-text-secondary">
          Only super admins can manage admin users.
        </p>
      </div>
    )
  }

  if (usersQuery.isLoading) {
    return <LoadingPage message="Loading admin users..." />
  }

  const users = usersQuery.data ?? []

  const columns = [
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      render: (row: AdminUserRecord) => (
        <span className="font-medium text-text-primary">{row.email}</span>
      ),
    },
    {
      key: 'name',
      label: 'Name',
      render: (row: AdminUserRecord) => row.name ?? '--',
    },
    {
      key: 'role',
      label: 'Role',
      render: (row: AdminUserRecord) => <StatusBadge status={row.role} />,
    },
    {
      key: 'last_login_at',
      label: 'Last Login',
      sortable: true,
      render: (row: AdminUserRecord) =>
        row.last_login_at ? new Date(row.last_login_at).toLocaleString() : 'Never',
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      render: (row: AdminUserRecord) => new Date(row.created_at).toLocaleDateString(),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row: AdminUserRecord) => (
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setChangingRole(row)
            }}
            className="rounded-lg p-1.5 text-text-secondary hover:bg-surface-2 hover:text-text-primary"
            title="Change role"
          >
            <Pencil className="h-4 w-4" />
          </button>
          {row.role !== 'super_admin' && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                setRemovingUser(row)
              }}
              className="rounded-lg p-1.5 text-text-secondary hover:bg-red-50 hover:text-red-600"
              title="Remove admin"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="h-6 w-6 text-admin-500" />
          <h1 className="text-2xl font-semibold text-text-primary">Admin Users</h1>
        </div>
        <button
          type="button"
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-admin-500 px-4 py-2 text-sm font-medium text-white hover:bg-admin-600"
        >
          <UserPlus className="h-4 w-4" />
          Add Admin
        </button>
      </div>

      <DataTable<AdminUserRecord>
        columns={columns}
        data={users}
        isLoading={usersQuery.isLoading}
        emptyMessage="No admin users found"
      />

      <AddAdminModal open={showAddModal} onClose={() => setShowAddModal(false)} />
      <RemoveAdminModal user={removingUser} onClose={() => setRemovingUser(null)} />
      <ChangeRoleModal user={changingRole} onClose={() => setChangingRole(null)} />
    </div>
  )
}
